/**
 * Submits a moderation action to /app/mod/action as a CSRF-signed POST.
 *
 * The endpoint merges query and body parameters, so an action is expressible
 * as a plain URL. The CSRF gate in Service/Web/Controller::before() runs only
 * for non-GET requests, and SameSite=Lax sends cookies on top-level GET
 * navigations, so a link the moderator clicks would carry no token check. The
 * route accepts POST only, and every control that names an action builds its
 * request here.
 *
 * Takes the action URL a control already carries, so the call sites keep
 * rendering an href that describes the action and stays inspectable.
 */

const CSRF_FIELD = 'csrf_token';
const REQUEST_KEY_FIELD = 'idempotency_key';
const ACTION_PATH = '/app/mod/action';

/**
 * Whether a URL names the moderation action endpoint on this origin.
 *
 * The controls are matched by an `href*=` selector, which also matches a URL
 * carrying the path on someone else's origin. Resolving to a same-origin
 * request would submit that URL's parameters with a valid token attached, so
 * anything not addressed to this endpoint is declined rather than rewritten.
 */
export function isSubmittableActionUrl(url, loc = window.location) {
    let parsed;

    try {
        parsed = new URL(url, loc.origin);
    } catch {
        return false;
    }

    return parsed.origin === loc.origin && parsed.pathname === ACTION_PATH;
}

/**
 * Whether a click on a moderation action link should be turned into a POST.
 *
 * `defaultPrevented` carries the decision of any handler that already ran on
 * the element. A handler that cancels the default without stopping propagation
 * has declined the action, and a delegated handler runs afterwards regardless,
 * so it would otherwise carry out what the first handler refused.
 */
export function shouldIntercept({defaultPrevented, url}, loc = window.location) {
    return !defaultPrevented && isSubmittableActionUrl(url, loc);
}

/**
 * Read the token the page renders for the session's CSRF cookie.
 */
export function readCsrfToken(doc = document) {
    const meta = doc.querySelector('meta[name="csrf_token"]');

    return meta ? (meta.getAttribute('content') || '') : '';
}

/**
 * Claim an idempotency key for the action the URL encodes.
 *
 * The key is minted against the parameters actually submitted, matching what
 * the form-POST path does. A missing idempotency module is not fatal: the
 * action still needs to reach the server, and duplicate suppression is a
 * separate concern from authenticity.
 *
 * `scope` is half the dedupe bucket the claim is stored under, so the same
 * action reaching the server by two routes only counts as one submission when
 * both name the same scope. The other two call sites (the form path in
 * moderate.js and photo-mod-modal.js) pass the action path as a literal, so
 * this passes the constant rather than the caller's parsed pathname.
 */
function claimRequestId(params, scope) {
    const idempotency = window.ModActionIdempotency;

    if (!idempotency || typeof idempotency.claim !== 'function') {
        return null;
    }

    const entries = params.map(([name, value]) => ({name, value}));

    return idempotency.claim(entries, scope).requestId;
}

function hiddenInput(doc, name, value) {
    const input = doc.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;

    return input;
}

/**
 * Build the POST form for an action URL without submitting it.
 *
 * Separated from post() so the shape is assertable without a live navigation.
 * Repeated parameters keep their bracketed names (`todo[]`, `user_ids[]`), so
 * PHP reads them as arrays exactly as it did from the query string.
 */
// Takes no `loc`, unlike the two predicates above. Resolution here only ever
// feeds the query parameters and a pathname that is pinned to a constant, so a
// different base could not change the request this builds. post() is the only
// caller and has already validated the URL against window.location.
export function buildActionForm(url, doc = document) {
    const actionUrl = new URL(url, window.location.origin);
    const params = Array.from(actionUrl.searchParams.entries());

    const form = doc.createElement('form');
    form.method = 'post';
    // Pinned to the constant rather than the parsed pathname. The controls are
    // matched by a substring selector, so a URL that merely contains the path
    // reaches here, and deriving the target from it would sign a POST to a
    // path this module never intended to write to.
    form.action = ACTION_PATH;

    // The two fields this function owns are dropped from the incoming URL, so
    // a source that already carried one cannot ship a second input under the
    // same name.
    const ownParams = params.filter(
        ([name]) => name !== REQUEST_KEY_FIELD && name !== CSRF_FIELD
    );

    ownParams.forEach(([name, value]) => {
        form.appendChild(hiddenInput(doc, name, value));
    });

    // Claimed against the same filtered list, so the drop above holds for the
    // key as well as the inputs. claimModerationAction() adopts an
    // idempotency_key it finds among the entries whenever session storage is
    // unavailable, so passing the raw list would let a URL supply the key that
    // decides whether the server treats this action as a duplicate. The intent
    // is unaffected either way, since requestIntent() filters both fields
    // itself, so this changes only which key is claimed.
    const requestId = claimRequestId(ownParams, ACTION_PATH);
    if (requestId) {
        form.appendChild(hiddenInput(doc, REQUEST_KEY_FIELD, requestId));
    }

    // form.submit() does not fire the submit event, so neither the delegated
    // mod-action handler nor the forms-csrf sweep sees this form. The token
    // has to be attached here. Omitted when the page rendered without the
    // meta tag, so the gate records token_missing rather than the
    // stale-form signature an empty value would report.
    const token = readCsrfToken(doc);
    if (token !== '') {
        form.appendChild(hiddenInput(doc, CSRF_FIELD, token));
    }

    return form;
}

/**
 * Submit an action URL as a POST.
 *
 * The response is a redirect back into the moderator UI, so this replaces a
 * navigation and nothing follows it on the current page.
 *
 * A refusal returns null, and the call sites read that as "fall back to the
 * href". That is the only correct reading for a URL this module declines to
 * sign, so nothing else may return null: an in-flight latch reported this way
 * would turn a double-click into the GET navigation the route rejects.
 */
export function post(url, doc = document) {
    if (!isSubmittableActionUrl(url)) {
        return null;
    }

    const form = buildActionForm(url, doc);
    form.hidden = true;
    doc.body.appendChild(form);

    // Called through the prototype because every parameter is appended as a
    // named input, and a control named `submit` shadows the form's own method
    // (HTMLFormElement is [LegacyOverrideBuiltIns]). The dispatcher takes
    // arbitrary parameters, so the collision is one rename away.
    const submit = window.HTMLFormElement
        ? window.HTMLFormElement.prototype.submit
        : null;

    if (submit) {
        submit.call(form);
    } else {
        form.submit();
    }

    // Detached once submitted. The navigation normally discards the document,
    // but a restored page would otherwise keep a form holding the full
    // payload and a valid token, reachable through document.forms and swept
    // by the token-refresh pass that walks every form on the page.
    if (typeof form.remove === 'function') {
        form.remove();
    }

    return form;
}

// post() and shouldIntercept() are the production surface. The other three are
// exported as test seams: buildActionForm() so the request shape is assertable
// without a live navigation, and the two predicates so the decline rules can be
// driven directly rather than inferred from a form that was never built.
window.ModActionSubmit = {
    post,
    shouldIntercept,
    buildActionForm,
    readCsrfToken,
    isSubmittableActionUrl,
};
